import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronRight, Globe2, LockKeyhole, Menu, Moon, Snowflake, Sun, X } from "lucide-react";
import { currentNavigation, isNavigationCurrent, navigation, normalizePath, privateNavigation } from "./navigation";
import { applyTheme, type Theme } from "./theme";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";

// One responsive shell and page rhythm for public tools and private views.
export const pageWidth = "site-content";
export const eyebrowClass = "page-eyebrow";
export const headingClass = "page-title";
export const pageHeadingClass = "page-title";
export const toolTitleClass = "page-title";
export const toolCopyClass = "page-copy";
export const mutedTextClass = "mt-1 block text-xs text-muted-foreground";

export const Nav = ({ path, privateWorkspace, onNavigate }: { path: string; privateWorkspace: boolean; onNavigate: () => void }) => (
  <nav aria-label="Main navigation" className="site-navigation">
    {(privateWorkspace ? privateNavigation : navigation).map((group) => (
      <section key={group.label}>
        <h2>{group.label}</h2>
        {group.items.map((item) => (
          <a key={item.href} href={item.href} aria-current={isNavigationCurrent(item, path) ? "page" : undefined} onClick={onNavigate}>
            <span className={`nav-icon nav-tone-${item.tone}`}><item.icon aria-hidden="true" /></span>
            <span>{item.label}</span>
          </a>
        ))}
      </section>
    ))}
  </nav>
);

export const Shell = ({ children }: { children: ReactNode }) => {
  const path = normalizePath(window.location.pathname);
  const privateWorkspace = window.location.hostname === "rent.winterr.dev" || path === "/tools/rent-breakdown";
  const current = privateWorkspace ? privateNavigation[0].items[0] : currentNavigation(path);
  const [menuOpen, setMenuOpen] = useState(false);
  const [theme, setTheme] = useState<Theme>(() => document.documentElement.dataset.theme === "light" ? "light" : "dark");
  const menuButton = useRef<HTMLButtonElement>(null);
  const closeMenu = () => { setMenuOpen(false); menuButton.current?.focus(); };
  useEffect(() => {
    if (!menuOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const close = (event: KeyboardEvent) => { if (event.key === "Escape") closeMenu(); };
    document.addEventListener("keydown", close);
    return () => { document.body.style.overflow = previous; document.removeEventListener("keydown", close); };
  }, [menuOpen]);
  return (
    <div className="site-shell" data-menu-open={menuOpen}>
      <a href="#main-content" className="skip-link">Skip to content</a>
      <div className="mobile-bar">
        <Button ref={menuButton} variant="ghost" size="icon" aria-label={menuOpen ? "Close navigation" : "Open navigation"} aria-expanded={menuOpen} aria-controls="site-sidebar" onClick={() => setMenuOpen(!menuOpen)}>{menuOpen ? <X /> : <Menu />}</Button>
        <a className="mobile-brand" href="/">winterr.dev</a>
        <span className="workspace-label">{privateWorkspace ? "Private" : "Workspace"}</span>
      </div>
      {menuOpen && <button className="nav-scrim" aria-label="Close navigation overlay" onClick={closeMenu} />}
      <aside id="site-sidebar" className="site-sidebar">
        <a className="site-brand" href="/"><span className="brand-mark"><Snowflake aria-hidden="true" /></span><span><strong>winterr.dev</strong><small>{privateWorkspace ? "Private workspace" : "Tools & intelligence"}</small></span></a>
        <Nav path={path} privateWorkspace={privateWorkspace} onNavigate={() => setMenuOpen(false)} />
        <div className="sidebar-end">
          <div className="sidebar-note"><span className="status-dot" />{privateWorkspace ? "Protected workspace" : "Source-linked tools"}</div>
          <div className="sidebar-actions"><Button variant="ghost" size="icon" aria-label={`Use ${theme === "dark" ? "light" : "dark"} theme`} onClick={() => { const next = theme === "dark" ? "light" : "dark"; applyTheme(next); setTheme(next); }}>{theme === "dark" ? <Sun /> : <Moon />}</Button><span>Built for the details.</span></div>
        </div>
      </aside>
      <div className="site-inset"><div className="site-surface">
        <header className="workspace-bar"><span>{privateWorkspace ? "Private workspace" : "Workspace"}<ChevronRight aria-hidden="true" /><strong>{current?.label ?? "Page"}</strong></span><span className="workspace-label">{privateWorkspace ? <LockKeyhole aria-hidden="true" /> : <Globe2 aria-hidden="true" />}{privateWorkspace ? "Private" : "Public"}</span></header>
        <main id="main-content" className={pageWidth} tabIndex={-1}>{children}</main>
        <footer className="site-footer"><span>winterr.dev</span><span>{privateWorkspace ? "Private data stays in your workspace." : "Public sources. Your own assumptions."}</span></footer>
      </div></div>
    </div>
  );
};

export const PageHeader = ({ eyebrow, title, children }: { eyebrow: string; title: ReactNode; children?: ReactNode }) => (
  <section className="page-header"><p className={eyebrowClass}>{eyebrow}</p><h1 className={pageHeadingClass}>{title}</h1>{children && <div className={toolCopyClass}>{children}</div>}</section>
);

export const ToolHeader = ({ eyebrow, title, copy, aside }: { eyebrow: string; title: ReactNode; copy: ReactNode; aside?: ReactNode }) => (
  <div className={cn("page-header tool-header", aside && "has-aside")}><div><p className={eyebrowClass}>{eyebrow}</p><h1 className={toolTitleClass}>{title}</h1><p className={toolCopyClass}>{copy}</p></div>{aside}</div>
);

export const SnapshotCard = ({
  label,
  value,
  description,
  action,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) => (
  <Card className={cn("relative", className)}>
    <CardHeader>
      <CardDescription>{label}</CardDescription>
      <CardTitle className="text-3xl">{value}</CardTitle>
      {description ? <CardDescription>{description}</CardDescription> : null}
      {action}
    </CardHeader>
  </Card>
);

export const StatCard = ({
  label,
  value,
  description,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  description?: ReactNode;
  className?: string;
}) => (
  <Card className={className}>
    <CardHeader>
      <CardDescription>{label}</CardDescription>
      <CardTitle className="text-2xl">{value}</CardTitle>
      {description ? <CardDescription className="leading-snug">{description}</CardDescription> : null}
    </CardHeader>
  </Card>
);

// A labelled breakdown block: heading + blurb above one sortable table.
export const TableSection = ({
  title,
  description,
  children,
  action,
}: {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  action?: ReactNode;
}) => (
  <section className="grid gap-3">
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 className="text-lg font-semibold leading-tight">{title}</h2>
        {description ? <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {action}
    </div>
    {children}
  </section>
);

export const SourcesSection = ({
  disclaimer,
  sources,
  footnote,
}: {
  disclaimer?: ReactNode;
  sources: ReadonlyArray<{ label: string; url: string }>;
  footnote?: ReactNode;
}) => (
  <section className="pt-9">
    <h2 className="text-lg font-semibold">Sources</h2>
    {disclaimer ? <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{disclaimer}</p> : null}
    <div className="my-4 flex flex-wrap gap-2">
      {sources.map((source) => (
        <Button key={source.url} nativeButton={false} render={<a href={source.url} rel="noreferrer" target="_blank" />} size="sm" variant="outline">
          {source.label}
        </Button>
      ))}
    </div>
    {footnote ? <p className={mutedTextClass}>{footnote}</p> : null}
  </section>
);
